import React, { useEffect, useState } from "react";
import ViewTable from "../../../Components/Common/ViewTable";
import { get, post, put, del, api } from "../../../utils/api";
import Cards from "../../../Components/Common/Cards";
import FilterModal from "../../../Components/Common/FilterModal";
import ActionButtons from "../../../Components/Common/ActionButtons";
import ViewData from "./ViewData";
import Form from "./Form";
import { Alert, Snackbar } from "@mui/material";
import BulkUploadModal from "../../../Components/Common/BulkUploadModal";
import DeleteAlertDialog from "../../../Components/Masters/DeleteAlertDialog";
import { useUser } from "../../../context/AuthContext";
import {
  shouldShowFacilityColumn,
  withFacilityViewTableHeader,
  mapTableDataWithFacility,
} from "../../../utils/facilityColumn";

const FuelEnergyActivities = () => {
  const { user } = useUser();
  const showFacilityColumn = shouldShowFacilityColumn(user);
  const isReadOnlyAdmin = Boolean(
    user?.companyAdmin || user?.isSubCompanyAdmin
  );
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [tableData, setTableData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [filterObj, setFilterObj] = useState({});
  const [filters, setFilters] = useState({
    capitalGoodsCategory: [],
    capitalGoodName: [],
  });
  const [deletedItemId, setDeletedItemId] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [bulkUploadModal, setBulkUploadModal] = useState(false);
  const [excelData, setExcelData] = useState(null);

  const [cardData, setCardData] = useState({
    totalEntries: 0,
    totalPurchasedFuelInKg: 0,
    totalPurchasedElectricity: 0,
    totalTransmissionAndDistributionLosses: 0,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const headers = withFacilityViewTableHeader([
    { key: "createdAt", label: "Date", sortable: true },
    { key: "consumptionPeriod", label: "Consumption Period", sortable: false },
    { key: "purchasedElectricityForResale.resoldQuantity", label: "Purchased Electricity for Resale", sortable: true },
  ], showFacilityColumn);

  const inputData = [
    { fieldName: "consumptionPeriod", title: "Consumption Period" },
    { fieldName: "purchasedFuels.naturalGas", title: "Natural Gas" },
    { fieldName: "purchasedFuels.coal", title: "Coal" },
    { fieldName: "purchasedFuels.petroleumGasoline", title: "Petrol" },
    { fieldName: "purchasedFuels.diesel", title: "Diesel" },
    { fieldName: "purchasedFuels.fuelOil", title: "Fuel Oil" },
    { fieldName: "purchasedFuels.lpg", title: "LPG" },
    { fieldName: "purchasedFuels.propane", title: "Propane" },
    { fieldName: "purchasedFuels.cng", title: "CNG" },
    { fieldName: "purchasedFuels.heatingAndCooling", title: "Heating and Cooling" },
    { fieldName: "purchasedFuels.steam", title: "Steam" },
    { fieldName: "purchasedElectricity.electricity", title: "Purchased Electricity" },
    {
      fieldName: "transmissionAndDistributionLosses.electricity",
      title: "Transmission and distribution losses - Electricity",
    },
    {
      fieldName: "purchasedElectricityForResale.purchased",
      title: "Have you purchased electricity for Resale this month?",
    },
    {
      fieldName: "purchasedElectricityForResale.resoldQuantity",
      title: "Purchased Electricity Resold Quantity",
    },
    { fieldName: "remark", title: "Remark (optional)" },
  ];

  const filterData = [
    { title: "Date", type: "date", key: "date", value: [] },
    {
      title: "Consumption Period",
      type: "month",
      key: "consumptionPeriod",
      value: [],
    },
  ];

  const fetchData = async () => {
    try {
      const filter = { ...filterObj };

      if (filter.date) {
        filter.startDate = filter?.date?.start;
        filter.endDate = filter?.date?.end || new Date();
        delete filter.date;
      }

      if (filter.consumptionPeriod) {
        const [startYear, startMonth] = String(
          filter.consumptionPeriod.start || ""
        ).split("-");
        const [endYear, endMonth] = String(
          filter.consumptionPeriod.end || ""
        ).split("-");

        if (startYear && startMonth) {
          filter.startYear = startYear;
          filter.startMonth = startMonth;
        }
        if (endYear && endMonth) {
          filter.endYear = endYear;
          filter.endMonth = endMonth;
        }
        delete filter.consumptionPeriod;
      }

      const queryParams = new URLSearchParams({
        ...filter,
        page: currentPage,
        limit: 10,
        sortField: sortConfig.key,
        sortOrder: sortConfig.direction,
      }).toString();

      const res = await get(`/scope3/fuelEnergyActivities/get?${queryParams}`);

      const arr =
        res?.data?.data?.map((item) => ({
          date: item?.date,
          consumptionPeriod: item?.consumptionPeriod,
          purchasedFuels: item?.purchasedFuels,
          purchasedElectricity: item?.purchasedElectricity,
          transmissionAndDistributionLosses:
            item?.transmissionAndDistributionLosses,
          action: "View",
          id: item?._id,
          ...item,
        })) || [];

      setTableData(arr);
      setTotalPages(res?.data?.totalPages || 1);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [sortConfig, filterObj, currentPage]);

  const fetchCardData = async () => {
    try {
      const res = await get(`/scope3/fuelEnergyActivities/cardData`);
      const data = res?.data?.data || {};
      setCardData({
        totalEntries: data?.totalEntries || 0,
        totalPurchasedFuelInKg: data?.totalPurchasedFuelInKg || 0,
        totalPurchasedElectricity: data?.totalPurchasedElectricity || 0,
        totalTransmissionAndDistributionLosses:
          data?.totalTransmissionAndDistributionLosses || 0,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchFilters = async () => {
    try {
      const res = await get(`/scope3/fuelEnergyActivities/filter`);
      void res;
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCardData();
  }, []);

  useEffect(() => {
    fetchExcelData();
  }, []);

  useEffect(() => {
    fetchFilters();
  }, []);

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (newFilterObj) => {
    setFilterObj(newFilterObj);
    setCurrentPage(1);
  };

  const fetchExcelData = () => {
    let arr = [];
    arr.push({
      "S No.": 1,
      "Date (DD/MM/YYYY)": "",
      "Consumption Month": "",
      Year: "",
      "Natural Gas": "",
      Coal: "",
      Petrol: "",
      Diesel: "",
      "Fuel Oil": "",
      LPG: "",
      Propane: "",
      "Heating and Cooling": "",
      Electricity: "",
      "T&D Losses": "",
      "Purchased for Resale": "Yes/No",
      "Purchased Electricity Resold Quantity": "",
      Remark: "",
      Unit: "Kwh",
    });

    setExcelData(arr);
  };

  const openModal = (type, dataForEdit) => {
    if (type === "add") {
      setIsModalOpen(true);
      setEditModal(false);
      setEditData({});
    } else if (type === "edit") {
      if (isReadOnlyAdmin) return;
      setIsModalOpen(true);
      setEditData(dataForEdit);
      setEditModal(true);
      setViewModal(false);
    }
  };

  const handleEdit = (data) => {
    openModal("edit", data);
  };

  const openViewModal = (data) => {
    setEditData(data);
    setViewModal(true);
  };

  const closeModal = (type) => {
    if (type === "add") {
      setIsModalOpen(false);
    } else if (type === "edit") {
      setIsModalOpen(false);
      setEditModal(false);
      setEditData({});
    }
  };

  const handleSubmit = async (data, isEditing) => {
    try {
      const url = isEditing
        ? `/scope3/fuelEnergyActivities/update?Id=${editData.id || editData._id}`
        : `/scope3/fuelEnergyActivities/add`;
  
      const apiCall = isEditing ? put : post;
  
      const res = await apiCall(url, data);
  
      if (res) {
        setsnackOpen({
          open: true,
          message: res?.data?.message,
          severity: "success",
        });
  
        setIsModalOpen(false);
  
        if (isEditing) {
          setEditModal(false);
          setEditData({});
        }
  
        fetchCardData();
        fetchData();
      }
    } catch (error) {
      setsnackOpen({
        open: true,
        message: error?.response?.data?.message || "Something went wrong",
        severity: "error",
      });
  
      console.error("Error in handleSubmit:", error);
    }
  };

  const handleDeleteOpen = (row) => {
    if (isReadOnlyAdmin) return;
    setDeletedItemId(row);
    setDeleteDialog(true);
  };



  return (
    <>
      <Cards
        data={[
          {
            title: "Total Purchased Electricity (KWH)",
            description: `${cardData?.totalPurchasedElectricity ?? 0}`,
          },
          {
            title: "Total Quantity of Fuel (Kg)",
            description: `${cardData?.totalPurchasedFuelInKg ?? 0}`,
          },
          {
            title: "Total Purchased Electricity Resold (KWH)",
            description: `${tableData.reduce(
              (sum, row) =>
                sum +
                Number(row?.purchasedElectricityForResale?.resoldQuantity ?? 0),
              0,
            )}`,
          },
        ]}
        title={
          "Fuel and Energy Related Activities"
        }
      />

      <ActionButtons
        onFilterClick={() => setIsFilterModalOpen(true)}
        onAddClick={() => openModal("add")}
        filterObj={filterObj}
        onUploadClick={() => setBulkUploadModal(true)}
        showUpload={false}
      />

      <ViewTable
        title={
          "Fuel and Energy Related Activities"
        }
        headers={headers}
        data={mapTableDataWithFacility(tableData, showFacilityColumn)}
        onSort={handleSort}
        sortConfig={sortConfig}
        page={currentPage}
        count={10}
        editIcon={isReadOnlyAdmin ? "disable" : "enable"}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onView={(row) => {
          openViewModal(row);
        }}
        onEdit={(row) => openModal("edit", row)}
        onDelete={(row) => handleDeleteOpen(row)}
      />

      <Snackbar
        open={snackOpen.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setsnackOpen({ ...snackOpen, open: false })}
      >
        <Alert
          onClose={() => setsnackOpen({ ...snackOpen, open: false })}
          severity={snackOpen.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackOpen.message}
        </Alert>
      </Snackbar>

      <Form
        isOpen={isModalOpen}
        onClose={() => closeModal(editModal ? "edit" : "add")}
        onSubmit={handleSubmit}
        header={
          editModal
            ? "Edit Fuel- and Energy-Related Activity"
            : "Add Fuel- and Energy-Related Activity"
        }
        initialData={editData}
        isEditing={editModal}
      />

      <ViewData
        id={editData ? editData.id || editData._id : ""}
        element={editData}
        title={"Fuel and energy"}
        viewData={viewModal}
        setViewData={setViewModal}
        setData={setEditData}
        inputData={inputData}
        handleEdit={handleEdit}
        companyAdmin={isReadOnlyAdmin}
      />

      {deleteDialog && (
        <DeleteAlertDialog
          {...{
            deleteDialog,
            setDeleteDialog,
            method: "delete",
            id: deletedItemId?.id || deletedItemId?._id,
            url: `${api}/scope3/fuelEnergyActivities/delete`,
            setsnackOpen,
            fetchData,
            fetchCardData,
          }}
        />
      )}

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filterData}
        onFilterChange={handleFilterChange}
        title="Filter"
      />

      {bulkUploadModal && (
        <BulkUploadModal
          onCloseBulkUpload={() => {
            setBulkUploadModal(false);
            fetchData();
            fetchCardData();
          }}
          isBulkUploadModal={bulkUploadModal}
          excelData={excelData}
          fileName={"fuelEnergyActivities"}
          setsnackOpen={setsnackOpen}
          url={"/scope3/fuelEnergyActivities/bulkupload"}
        />
      )}
    </>
  );
};

export default FuelEnergyActivities;
